<template>
  <v-dialog v-model="visible" max-width="1100px">
    <v-card class="modern-modal">
      <v-card-title class="text-h6 modern-modal-title">
        {{ $t("Link Group") }}
        <v-spacer />
        <!-- Takes the selected student(s) OUT of whichever group they are in. Only their own rows are
             detached — no group is deleted, the groups only have their student counts recomputed. Shown
             only for a parent that passes an unlink endpoint, so the other lists sharing this dialog are
             unaffected. Needs no group picked below: this is about leaving, not joining. -->
        <v-btn
          v-if="unlinkApi && effectiveIds.length"
          :loading="unlinking"
          :disabled="saving"
          color="error"
          outlined
          small
          @click="confirmUnlink = true"
        >
          <v-icon left small>mdi-close-circle-outline</v-icon>
          {{ $t("Remove Group") }}
        </v-btn>
      </v-card-title>

      <v-card-text>
        <v-text-field
          v-model="search"
          :placeholder="$t('Search Groups...')"
          append-icon="mdi-magnify"
          dense
          hide-details
          outlined
          clearable
          @click:append="onSearch"
          @keyup.enter="onSearch"
        />

        <v-radio-group v-model="selected" class="ma-0 pa-0">
          <v-data-table
            hide-default-footer
            :loading="loading"
            :headers="headers"
            :items="groups"
            item-key="id"
            class="cursor-pointer"
            @click:row="onRowClick"
          >
            <template v-slot:item.select="{ item }">
              <v-radio dense :value="item.id" @click.stop />
            </template>

            <template v-slot:item.educationType.name="{ item }">
              {{ item.educationType.name }}
            </template>
            <template v-slot:item.educationLanguage.name="{ item }">
              {{ item.educationLanguage.name }}
            </template>
            <template v-slot:item.educationFaculty.name="{ item }">
              {{ item.educationFaculty.name }}
            </template>
            <template v-slot:item.countOfStudentsInGroup="{ item }">
              {{ item.countOfStudentsInGroup }}
            </template>
            <template v-slot:item.countOfMaleStudentsInGroup="{ item }">
              {{ item.countOfMaleStudentsInGroup }}
            </template>
             <template v-slot:item.countOfFemaleStudentsInGroup="{ item }">
              {{ item.countOfFemaleStudentsInGroup }}
            </template>
          </v-data-table>
        </v-radio-group>

        <div class="pt-2 pb-2">
          <v-pagination
            v-model="page"
            :total-visible="5"
            :length="Math.ceil(total / pageSize) || 1"
            @input="onPage"
          />
        </div>

        <div v-if="!effectiveIds.length" class="hint">
          {{ $t('Select a contract or pass :selected-rows to enable saving.') }}
        </div>
      </v-card-text>

      <v-card-actions class="modern-modal-actions">
        <v-spacer />
        <v-btn text class="cancel-button" :disabled="saving" @click="$emit('cancel')">
          <v-icon left small>mdi-close</v-icon>
          {{ $t("Cancel") }}
        </v-btn>
        <v-btn
          :disabled="saveDisabled"
          :loading="saving"
          class="modern-button modern-accent"
          @click="onSave"
        >
          <v-icon left small>mdi-check</v-icon>
          {{ $t("Save") }}
        </v-btn>
      </v-card-actions>
    </v-card>

    <app-confirm
      v-model="confirmUnlink"
      :text="$t('Remove the selected students from their group?')"
      @accept="onUnlink"
    />
  </v-dialog>
</template>

<script>
import axios from 'axios';
import AppConfirm from './modals/app-confirm.vue';

export default {
  name: 'LinkGroupDialog',
  components: { AppConfirm },
  props: {
    value: { type: Boolean, default: false },
    initialSelected: { type: [Number, String], default: null },
    contractId: { type: [Number, String], default: null },
    selectedRows: { type: Array, default: () => [] },
    listApi: { type: String, default: '/api/groups' },
    linkApi: { type: String, default: '/api/groups/link/group/2025' },
    // When provided, dialog uses this single POST (body = ids) instead of looping
    // per-id, so Select All with 1000s of rows takes one request.
    bulkLinkApi: { type: String, default: '' },
    // Detach endpoints. Empty by default, which is what hides the button for the lists that have not
    // been given one — the dialog is shared by six of them.
    unlinkApi: { type: String, default: '' },
    bulkUnlinkApi: { type: String, default: '' },
  },
  data() {
    return {
      visible: this.value,
      search: null,
      groups: [],
      headers: [
        { text: '', value: 'select', sortable: false },
        { text: this.$t('Name'), value: 'code' },
        { text: this.$t('Education Type'), value: 'educationType.name' },
        { text: this.$t('Language'), value: 'educationLanguage.name' },
        { text: this.$t('Faculty'), value: 'educationFaculty.name' },
        { text: this.$t('Students in Group'), value: 'countOfStudentsInGroup' },
        { text: this.$t('Male students'), value: 'countOfMaleStudentsInGroup' },
        { text: this.$t('Female students'), value: 'countOfFemaleStudentsInGroup' },
      ],
      loading: false,
      saving: false,
      unlinking: false,
      confirmUnlink: false,
      page: 1,
      pageSize: 10,
      total: 0,
      selected: this.initialSelected ?? null,
    };
  },
  computed: {
    effectiveIds() {
      if (this.contractId) return [Number(this.contractId)];
      if (!Array.isArray(this.selectedRows)) return [];
      return this.selectedRows
        .map((r) => Number(typeof r === 'object' ? r?.id : r))
        .filter((n) => Number.isFinite(n));
    },
    saveDisabled() {
      return (
        this.saving ||
        this.selected === null ||
        this.selected === undefined ||
        this.selected === '' ||
        this.effectiveIds.length === 0
      );
    },
  },
  watch: {
    value(val) {
      this.visible = val;
      if (val) {
        this.selected = this.initialSelected ?? null;
        this.page = 1;
        this.fetchGroups();
      }
    },
    visible(val) {
      if (!val) this.$emit('input', false);
    },
    initialSelected(val) {
      this.selected = val ?? null;
    },
  },
  methods: {
    async fetchGroups() {
      this.loading = true;
      try {
        const { data } = await axios.get(this.listApi, {
          params: {
            page: this.page - 1,
            size: this.pageSize,
            sort: 'code,asc',
            search: this.search || undefined,
          },
        });
        this.groups = data?.content || [];
        this.total = data?.totalElements || 0;
      } finally {
        this.loading = false;
      }
    },
    onSearch() {
      this.page = 1;
      this.fetchGroups();
    },
    onPage() {
      this.fetchGroups();
    },
    onRowClick(item) {
      this.selected = item?.id;
    },
    /**
     * Detach the selected contracts from their groups. Mirrors onSave: one POST for a selection when the
     * parent gives a bulk endpoint, otherwise one per contract. The list is refreshed through the same
     * 'linked' event the parent already listens to.
     */
    async onUnlink() {
      const ids = this.effectiveIds;
      if (this.unlinking || !this.unlinkApi || !ids.length) return;
      this.unlinking = true;

      try {
        if (this.bulkUnlinkApi && ids.length > 1) {
          await axios.post(this.bulkUnlinkApi, ids);
        } else {
          for (const id of ids) {
            await axios.post(this.unlinkApi, null, { params: { contractId: id } });
          }
        }

        this.$emit('linked', { groupId: null, contractIds: ids });
        this.visible = false;
        this.$emit('input', false);
      } catch (e) {
        console.error('Failed to remove group:', e);
        this.$emit('error', e);
      } finally {
        this.unlinking = false;
      }
    },

    async onSave() {
      if (this.saveDisabled) return;
      this.saving = true;

      const groupId = Number(this.selected);
      const ids = this.effectiveIds;

      try {
        if (this.bulkLinkApi && ids.length > 1) {
          await axios.post(this.bulkLinkApi, ids, { params: { groupId } });
        } else {
          for (const id of ids) {
            await axios.post(this.linkApi, null, {
              params: { groupId, contractId: id },
            });
          }
        }

        this.$emit('linked', { groupId, contractIds: ids });
        this.visible = false;
        this.$emit('input', false);
      } catch (e) {
        console.error('Failed to link group:', e);
        this.$emit('error', e);
      } finally {
        this.saving = false;
      }
    },
  },
};
</script>

<style scoped lang="scss">
.modern-modal {
  border-radius: 12px;
  padding: 12px;
  box-shadow: 0 8px 24px rgba(0, 0, 0, 0.2);
}
.modern-modal-title {
  display: flex;
  align-items: center;
  gap: 8px;
}
.modern-modal-actions {
  padding-top: 12px;
}
.modern-button {
  text-transform: none;
  border-radius: 8px;
  padding: 8px 20px;
  font-weight: 500;
  display: flex;
  align-items: center;
  gap: 6px;
}
.modern-primary {
  background: #1d6f42 !important;
  color: #fff;
}
.modern-accent {
  background-color: #ffd451 !important;
  color: #111318 !important;
  box-shadow: none !important;
  border: 2px solid #ffd451 !important;
  font-weight: 600 !important;
  letter-spacing: 0.02em;
  .v-icon { color: #111318 !important; }

  &.v-btn--disabled {
    background-color: #ffd451 !important;
    color: #111318 !important;
    opacity: 0.45;
    .v-icon { color: #111318 !important; }
  }
}
.cancel-button {
  color: #555;
  font-weight: 500;
  display: flex;
  align-items: center;
  gap: 6px;
}
.cursor-pointer {
  cursor: pointer !important;
}
.hint {
  font-size: 12px;
  color: #666;
  margin-top: 4px;
}
</style>
